import { useCallback, useMemo } from 'react';
import type { Key } from 'react';

import type { IdOption } from '../types';

export const useIdOptionsResolver = <
  TId extends Key,
  TOption extends IdOption<TId>,
>(
  options: readonly TOption[],
) => {
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );

  const ids = useMemo(() => options.map((option) => option.id), [options]);

  const getOptionLabel = useCallback(
    (id: TId) => optionById.get(id)?.label ?? String(id),
    [optionById],
  );

  const getOptionDisabled = useCallback(
    (id: TId) => optionById.get(id)?.disabled ?? false,
    [optionById],
  );

  const hasGroups = options.some((option) => option.group !== undefined);

  const getOptionGroup = useCallback(
    (id: TId) => optionById.get(id)?.group ?? '',
    [optionById],
  );

  return {
    getOptionDisabled,
    getOptionGroup: hasGroups ? getOptionGroup : undefined,
    getOptionLabel,
    ids,
    optionById,
  };
};
